import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { AnchorChip, DocOverlay, MarkdownView, WalkCaptureLayer, docExcerpt, docMetaLine, formatNoteTime, useAnchorFocus, walkPointFromEvent } from "../DeliverySections";
import { DocDiffView } from "../DocDiff";
import { EmailsCopyView } from "../sections/EmailsSection";
import { WalkFeedback, type WalkPoint } from "../WalkFeedback";
import { transcribeLive } from "../VoiceableInput";
import {
  classifySendFailure,
  classifyStartFailure,
  voiceFailureMessageKey,
  type VoiceFailure
} from "../voice-recording";
import { translateCount } from "../i18n";
import {
  editRequestLooksStuck,
  editRequestSurface,
  mergeOptimisticRequest,
  noteChipState,
  sortNotesByLifecycle,
  type NoteChipState,
  type OptimisticEditRequest
} from "../staff-feedback";
import { confirmForClient, makeSafeT } from "./core";
import type { StaffPaneProps } from "./console-types";
import { materialViewSource } from "./index";
import { publishIssueKey } from "./publish-issue";
import { StaffNoteChip, StaffNoteText, StaffVoiceNote, staffAudioUrl, staffChipTitle } from "./FeedbackNotes";
// The partner's material room (Pavel 2026-07-23): the SAME reading experience
// the client has (doc reader / full-screen live page), with a panel where the
// partner talks DIRECTLY to Alex - typed or dictated instructions plus the
// client's annotations - and the material changes before their eyes: while
// Alex works we poll, and when the new version lands the reader swaps to it.
export function StaffMaterialOverlay(props: StaffPaneProps & { kind: string; onClose: () => void }) {
  const t = props.t;
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const returnFocusRef = useRef<Pick<HTMLElement, "focus" | "isConnected"> | null>(
    typeof document !== "undefined" && document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  );
  useEffect(() => {
    // Closing the room must land the keyboard back on its opener. The saved
    // node can be REPLACED by a re-render after close, so a selector fallback
    // finds the fresh "Otevřít revizní místnost" button (audit a2-04).
    const kind = props.kind;
    return () => restoreFocus(returnFocusRef.current, `[data-review-room-open="${kind}"]`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const focus = () => focusReviewRoom(closeButtonRef.current);
    queueMicrotask(focus);
    const frame = window.requestAnimationFrame(focus);
    const settled = window.setTimeout(focus, 60);
    const onKey = (event: KeyboardEvent) => trapReviewRoomFocus(event);
    document.addEventListener("keydown", onKey);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settled);
      document.removeEventListener("keydown", onKey);
    };
  }, []);
  const rows = props.detail.deliverables
    .filter((item) => String(item.kind) === props.kind)
    .sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0));
  const current = rows[0];
  const [instruction, setInstruction] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  // A named publish refusal prints its sentence in the publish strip; anything
  // unnamed stays with the console's generic toast (publish-issue.ts).
  const [publishIssue, setPublishIssue] = useState<string | null>(null);
  const [voicePhase, setVoicePhase] = useState<"idle" | "recording" | "transcribing">("idle");
  // F98: the partner's mic used to fail into "idle" with no message at all, so
  // the button read as dead. The cause is named like everywhere else.
  const [voiceFailure, setVoiceFailure] = useState<VoiceFailure | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [seenVersion, setSeenVersion] = useState<number>(current ? Number(current.version ?? 0) : 0);
  const [updated, setUpdated] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);
  // "What changed" reading mode: diff of the viewed version against its
  // predecessor, so the partner sees Alex's edit instead of re-reading.
  const [showDiff, setShowDiff] = useState(false);
  // Partner "Mluv a ukazuj" over the material (Pavel 2026-07-24): same walk
  // experience the client has, saved as a note, then SELECTED notes go to Alex.
  const [walkActive, setWalkActive] = useState(false);
  const walkPointerRef = useRef<((point: Omit<WalkPoint, "t">) => void) | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const focusAnchor = useAnchorFocus();
  const [selectedNotes, setSelectedNotes] = useState<Set<string>>(new Set());
  const seenNoteIds = useRef<Set<string>>(new Set());
  const [typedOpen, setTypedOpen] = useState(false);
  const [typedNote, setTypedNote] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  // Deep-state focus (audit a2-06): opening a disclosure moves focus INTO its
  // textarea; cancelling returns it to the toggle that opened it. Without
  // this, Enter dropped focus on BODY and Tab restarted at the panel top.
  const typedAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const typedToggleRef = useRef<HTMLButtonElement | null>(null);
  const messageAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const messageToggleRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (typedOpen) typedAreaRef.current?.focus();
  }, [typedOpen]);
  useEffect(() => {
    if (messageOpen) messageAreaRef.current?.focus();
  }, [messageOpen]);
  // One retry after paint: the toggle button re-mounts when the disclosure
  // closes, and a single rAF could still miss it (audit a2-04).
  const focusToggle = (ref: { current: HTMLButtonElement | null }) => {
    window.requestAnimationFrame(() => {
      ref.current?.focus();
      window.setTimeout(() => {
        if (document.activeElement === document.body) ref.current?.focus();
      }, 60);
    });
  };
  const closeTypedNote = () => {
    setTypedOpen(false);
    setTypedNote("");
    focusToggle(typedToggleRef);
  };
  const closeMessage = () => {
    setMessageOpen(false);
    setInstruction("");
    focusToggle(messageToggleRef);
  };

  // Server truth of the edit request, overlaid with the optimistic echo of a
  // send THIS overlay just made: the chips and the status strip flip the
  // moment the partner clicks, and the detail poll swaps in the server
  // resource as soon as it lands.
  const serverSurface = editRequestSurface(props.detail.resources, props.detail.deliverables);
  const [optimistic, setOptimistic] = useState<OptimisticEditRequest | null>(null);
  useEffect(() => {
    if (optimistic && serverSurface && serverSurface.status === "new") setOptimistic(null);
  }, [optimistic, serverSurface]);
  const merged = mergeOptimisticRequest(serverSurface, optimistic);
  const mine = merged && merged.kind === props.kind ? merged : null;
  const processing = mine?.status === "new";

  // Live loop: poll while Alex works; a version bump swaps the open reader.
  const refresh = props.refresh;
  useEffect(() => {
    if (!processing) return;
    const timer = window.setInterval(() => { void refresh(); }, 8000);
    return () => window.clearInterval(timer);
  }, [processing, refresh]);
  const currentVersion = current ? Number(current.version ?? 0) : 0;
  useEffect(() => {
    if (currentVersion > seenVersion) { setSeenVersion(currentVersion); setUpdated(true); }
  }, [currentVersion, seenVersion]);

  // Match notes by KIND: a superseded (unpublished) version disappears from
  // the partner's deliverable rows, but its worked-in notes must stay visible.
  const notes = props.detail.feedback.filter((row) =>
    String(row.deliverableKind ?? "") === props.kind
    || rows.some((item) => String(item.id) === String(row.deliverableId)));
  const openNoteIds = notes.filter((note) => String(note.status) === "new").map((note) => String(note.id));
  // New open notes arrive pre-selected; the partner UNchecks what should wait.
  const openNotesKey = openNoteIds.join("|");
  useEffect(() => {
    setSelectedNotes((currentSel) => {
      const next = new Set<string>();
      for (const id of openNoteIds) if (currentSel.size === 0 || currentSel.has(id) || !seenNoteIds.current.has(id)) next.add(id);
      for (const id of openNoteIds) seenNoteIds.current.add(id);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNotesKey]);

  async function saveTypedNote() {
    if (!current || !typedNote.trim()) return;
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/deliverables/${String(current.id)}/feedback`,
      { method: "POST", body: { text: typedNote.trim() } }
    );
    if (response.ok) { setTypedNote(""); setTypedOpen(false); }
  }

  // One send core for the composer and for "Poslat znovu": the optimistic
  // echo flips the UI at the CLICK, a failed request rolls it back and says
  // so. Never a silent nothing (walk 2026-07-27).
  async function queueEdit(body: { deliverableId: string; instruction?: string; noteIds: string[] }): Promise<boolean> {
    setSending(true);
    setSendError(false);
    setUpdated(false);
    const echo: OptimisticEditRequest = {
      deliverableId: body.deliverableId,
      kind: props.kind,
      noteIds: body.noteIds,
      instruction: body.instruction ?? null,
      requestedAt: new Date().toISOString()
    };
    setOptimistic(echo);
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/sales-edit`, {
      method: "POST",
      body
    });
    setSending(false);
    if (!response.ok) {
      setOptimistic((value) => (value === echo ? null : value));
      setSendError(true);
      return false;
    }
    return true;
  }

  async function sendInstruction() {
    const noteIds = [...selectedNotes];
    if (!current || (!instruction.trim() && noteIds.length === 0)) return;
    const delivered = await queueEdit({ deliverableId: String(current.id), instruction: instruction.trim() || undefined, noteIds });
    if (delivered) { setInstruction(""); setSelectedNotes(new Set()); setMessageOpen(false); }
  }

  // Re-send of a failed or stuck request: same instruction, same notes (minus
  // the ones no longer open), always targeting the current version.
  const resendNoteIds = mine
    ? [...mine.noteIds].filter((noteId) => openNoteIds.includes(noteId))
    : [];
  const canResend = mine !== null && (Boolean(mine.instruction) || resendNoteIds.length > 0);
  async function resendRequest() {
    if (!current || !mine || !canResend) return;
    await queueEdit({
      deliverableId: String(current.id),
      instruction: mine.instruction ?? undefined,
      noteIds: resendNoteIds
    });
  }

  async function startVoice() {
    if (recorderRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setVoiceFailure("unsupported");
      return;
    }
    setVoiceFailure(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => { if (event.data.size) chunksRef.current.push(event.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        recorderRef.current = null;
        void transcribeIntoComposer(new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" }));
      };
      recorderRef.current = recorder;
      recorder.start();
      setVoicePhase("recording");
    } catch (error) {
      recorderRef.current = null;
      setVoicePhase("idle");
      setVoiceFailure(classifyStartFailure(error));
    }
  }

  async function transcribeIntoComposer(blob: Blob) {
    setVoicePhase("transcribing");
    try {
      const text = await transcribeLive(blob, blob.type || "audio/webm", props.csrfToken ?? "");
      if (text) setInstruction((value) => (value.trim() ? `${value.trim()}\n${text}` : text));
      else setVoiceFailure("empty");
    } catch (error) {
      // Dictation is best effort (typing still works), but a silent failure
      // reads as a broken button.
      setVoiceFailure(classifySendFailure(error, navigator.onLine !== false));
    }
    setVoicePhase("idle");
  }

  if (!current) return null;
  // Publish gate (meeting 24.7. D8): the newest version may still be parked.
  // The overlay says truthfully what the client sees and offers ONE primary
  // action - "Zveřejnit klientovi" - to swap the live version.
  const currentPublished = current.published === 1 || current.published === true;
  const liveVersion = rows.find((row) => row.published === 1 || row.published === true) ?? null;
  const currentStatus = String(current.status ?? "");
  const publishable = !currentPublished && ["review", "approved"].includes(currentStatus);
  async function publishCurrent() {
    if (!current || !confirmForClient(props, "staffConsole.confirmDeliverablePublish")) return;
    setPublishIssue(null);
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/deliverables/${String(current.id)}/publish`,
      { method: "POST", body: {} }
    );
    setPublishIssue(await publishIssueKey(response));
  }
  // Version history: the partner can flip to an older version read-only
  // (compare what Alex changed); instructions always target the current one.
  const viewed = rows.find((row) => String(row.id) === viewId) ?? current;
  const viewingOld = String(viewed.id) !== String(current.id);
  // Page vs document decision lives in materialViewSource (guarded by
  // partner-os-phase-workspace tests): a real page URL wins over the
  // builder's internal contentMd summary.
  const view = materialViewSource(
    typeof viewed.urlOrR2 === "string" ? viewed.urlOrR2 : "",
    typeof viewed.contentMd === "string" ? viewed.contentMd : null
  );
  const contentMd = view.mode === "doc" ? view.contentMd : null;
  // The raw hub URL sits behind Cloudflare Access, which the partner cannot
  // pass; the iframe goes through the worker proxy (session here, service
  // token there). The admin edit form keeps showing the RAW urlOrR2.
  const url = view.mode === "page"
    ? `/api/staff/clients/${props.detail.client.id}/deliverables/${String(current.id)}/page`
    : "";
  const viewedIndex = rows.findIndex((row) => String(row.id) === String(viewed.id));
  const predecessor = viewedIndex >= 0 ? rows[viewedIndex + 1] : undefined;
  const previousMd = predecessor && typeof predecessor.contentMd === "string" && predecessor.contentMd ? predecessor.contentMd : null;
  const diffAvailable = Boolean(contentMd && previousMd);

  const overlayChipFor = (note: (typeof notes)[number]) => noteChipState(
    { id: note.id, status: note.status, deliverableId: note.deliverableId, createdAt: note.createdAt },
    mine
  );
  const openNotes = sortNotesByLifecycle(
    notes.filter((note) => String(note.status) === "new"),
    overlayChipFor
  );
  const resolvedNotes = sortNotesByLifecycle(
    notes.filter((note) => String(note.status) !== "new"),
    overlayChipFor
  );
  const noteCard = (note: (typeof notes)[number]) => {
    const id = String(note.id);
    const chip = overlayChipFor(note);
    const open = chip === "new";
    const sentToAlex = chip === "sent" || chip === "working";
    return (
      <li key={id} className={`doc-note${sentToAlex ? " note-sent" : ""}`}>
        <div className="doc-note-head">
          {open && !viewingOld && (
            <input
              type="checkbox"
              className="note-select"
              aria-label={t("staffConsole.material.selectNote")}
              checked={selectedNotes.has(id)}
              disabled={processing || sending}
              onChange={(event) => setSelectedNotes((currentSel) => {
                const next = new Set(currentSel);
                if (event.target.checked) next.add(id); else next.delete(id);
                return next;
              })}
            />
          )}
          {typeof note.createdAt === "string" && (
            <time dateTime={String(note.createdAt)}>{formatNoteTime(String(note.createdAt))}</time>
          )}
          {typeof note.displayName === "string" && <span className="doc-note-author">{String(note.displayName)}</span>}
          <StaffNoteChip state={chip} t={t} title={staffChipTitle(chip, mine, t)} />
        </div>
        {(note.text ?? note.transcript) != null
          ? <StaffNoteText text={String(note.text ?? note.transcript)} t={t} />
          : <StaffVoiceNote text={null} audioUrl={staffAudioUrl(props.detail.client.id, note)} transcriptStatus={typeof note.transcriptStatus === "string" ? note.transcriptStatus : null} t={t} />}
        <p className="doc-note-state-hint">{t(`staffConsole.material.noteHint.${chip}`)}</p>
        {contentMd && Array.isArray(note.walkAnchors) && note.walkAnchors.length > 0 && (
          <AnchorChip
            count={(note.walkAnchors as string[]).length}
            label={t("staffConsole.material.anchorHint")}
            onFocus={() => focusAnchor(id, note.walkAnchors as string[], false)}
            onActivate={() => focusAnchor(id, note.walkAnchors as string[], true)}
          />
        )}
        {/* F163 (Tomas 2026-09-22): the review room had no way to take a note
            back before it went to Alex; the same two actions the feedback
            pane has. "Neni relevantni" closes any open note as what it is
            (a client's sentence is never deleted). "Smazat" removes a STAFF
            note: the author's own, or any staff note for an administrator. */}
        {open && !sentToAlex && (
          <div className="doc-note-foot">
            <button className="quiet-link note-dismiss" type="button" disabled={processing || sending} onClick={() => void props.run(
              `/api/staff/clients/${props.detail.client.id}/feedback/${id}`,
              { method: "PATCH", body: { status: "dismissed" } }
            )}>{t("staffConsole.notes.dismiss")}</button>
            {noteDeletable(note) && (
              <button className="quiet-link note-delete" type="button" disabled={processing || sending} onClick={() => {
                if (!confirmForClient(props, "staffConsole.notes.deleteConfirm")) return;
                void props.run(`/api/staff/clients/${props.detail.client.id}/feedback/${id}`, { method: "DELETE" });
              }}>{t("staffConsole.notes.delete")}</button>
            )}
          </div>
        )}
      </li>
    );
  };
  function noteDeletable(note: (typeof notes)[number]): boolean {
    const authorId = String(note.userId ?? "");
    const own = Boolean(props.currentUserId) && authorId === props.currentUserId;
    const staffNote = typeof note.staffRole === "string" && note.staffRole.length > 0;
    return own || (props.role === "admin" && staffNote);
  }
  const sendLabel = selectedNotes.size > 0 && instruction.trim()
    ? translateCount(props.locale, "staffConsole.material.sendNotesAndMessageCount", selectedNotes.size)
    : selectedNotes.size > 0
      ? translateCount(props.locale, "staffConsole.material.sendNotesCount", selectedNotes.size)
      : instruction.trim()
        ? t("staffConsole.material.sendMessageOnly")
        : t("staffConsole.material.send");
  const allSelected = openNoteIds.length > 0 && openNoteIds.every((id) => selectedNotes.has(id));
  // A closed control says WHY it is closed (wave 4 law). "Poslat Alexovi" sat
  // disabled with no title, no aria-disabled and no sentence, under a toggle
  // that called the message "(volitelné)" - so the partner read "nothing is
  // required" over a button that refused every click (audit P-06). The empty
  // state now names what is missing; `processing` and `sending` already speak
  // through the button's own label and the status strip above.
  const sendEmpty = selectedNotes.size === 0 && !instruction.trim();
  const sendReason = sendEmpty && !processing && !sending;
  const aside = (
    <div className="gate-doc-actions alex-panel">
      {/* 1. STATUS: one line that always says what is happening right now. */}
      <div className="alex-status-strip">
        {publishable && !processing && (
          <div className="publish-gate-strip" role="status">
            {/* A built-but-unpublished version is the partner's turn, not a
                warning: blue, like every other "you decide" (audit P-15). */}
            <p className="message old-version-note publish-pending">
              {t("staffConsole.material.unpublished").replace("{version}", String(current.version))}{" "}
              {liveVersion
                ? t("staffConsole.material.clientSees").replace("{version}", String(liveVersion.version))
                : t("staffConsole.material.clientSeesNothing")}
            </p>
            {/* Publishing releases the CURRENT version. While the partner
                reads an older one, the button stays off so nobody publishes
                something different from what they see (audit a2-09). */}
            <button
              className="primary-button compact-button"
              disabled={viewingOld}
              title={viewingOld ? t("staffConsole.material.publishBackToCurrent") : undefined}
              type="button"
              onClick={() => void publishCurrent()}
            >
              {t("staffConsole.material.publishVersion").replace("{version}", String(current.version))}
            </button>
            {publishIssue && (
              <p className="message error-message" role="alert">{t(publishIssue)}</p>
            )}
          </div>
        )}
        {processing && mine ? (
          // The request line always says WHERE the edit is: queued (sent,
          // editor not there yet) or working (Alex fetched it), with the
          // exact time. A request unmoved too long is called out honestly
          // with a re-send instead of pulsing forever.
          <div className="alex-request-line" role="status">
            <p className="alex-status">
              <span className="loading-pulse" />
              {t(mine.servedAt ? "staffConsole.material.working" : "staffConsole.material.queued")}
            </p>
            <small className="alex-request-meta">
              {mine.servedAt
                ? t("staffConsole.material.workingSince").replace("{time}", formatNoteTime(mine.servedAt))
                : mine.requestedAt
                  ? t("staffConsole.material.sentAt").replace("{time}", formatNoteTime(mine.requestedAt))
                  : ""}
            </small>
            {editRequestLooksStuck(mine, new Date().toISOString()) && (
              <p className="message old-version-note alex-stuck">
                {t("staffConsole.material.stuck")}
                {canResend && (
                  <>{" "}<button className="quiet-link" type="button" disabled={sending} onClick={() => void resendRequest()}>{t("staffConsole.material.retry")}</button></>
                )}
              </p>
            )}
          </div>
        ) : mine?.status === "failed" ? (
          <div className="alex-request-line failed" role="alert">
            <p className="message error-message">{t("staffConsole.salesEdit.failed")} {mine.failNote ?? ""}</p>
            {canResend && (
              <button className="secondary-button compact-button" type="button" disabled={sending} onClick={() => void resendRequest()}>
                {t("staffConsole.material.retry")}
              </button>
            )}
          </div>
        ) : mine?.status === "done" && mine.newDeliverableId === String(current.id) ? (
          // Persistent close of the arc: survives remounts (it reads the
          // resource, not ephemeral component state).
          <p className="message success-message" role="status">
            {t("staffConsole.material.doneStatus").replace("{version}", String(current.version))}
            {mine.resolvedAt ? ` (${formatNoteTime(mine.resolvedAt)})` : ""}
            {diffAvailable && !showDiff && !viewingOld && (
              <>{" "}<button className="quiet-link" type="button" onClick={() => setShowDiff(true)}>{t("staffConsole.material.updatedShowChanges")}</button></>
            )}
          </p>
        ) : updated ? (
          <p className="message success-message" role="status">
            {t("staffConsole.material.updated").replace("{version}", String(current.version))}
            {diffAvailable && !showDiff && !viewingOld && (
              <>{" "}<button className="quiet-link" type="button" onClick={() => setShowDiff(true)}>{t("staffConsole.material.updatedShowChanges")}</button></>
            )}
          </p>
        ) : null}
        {rows.length > 1 && (
          <div className="version-strip" role="tablist" aria-label={t("staffConsole.material.versions")}>
            {rows.slice(0, 8).map((row) => (
              <button
                key={String(row.id)}
                type="button"
                role="tab"
                aria-selected={String(row.id) === String(viewed.id)}
                className={`version-chip${String(row.id) === String(viewed.id) ? " active" : ""}`}
                onClick={() => setViewId(String(row.id) === String(current.id) ? null : String(row.id))}
              >
                {`v${String(row.version)}`}
              </button>
            ))}
            {diffAvailable && (
              <button
                type="button"
                className={`version-chip diff-toggle${showDiff ? " active" : ""}`}
                aria-pressed={showDiff}
                onClick={() => setShowDiff((value) => !value)}
              >
                {t("staffConsole.material.showChanges")}
              </button>
            )}
          </div>
        )}
        {viewingOld && (
          <p className="message old-version-note" role="status">
            {t("staffConsole.material.oldVersion").replace("{version}", String(viewed.version))}{" "}
            <button className="quiet-link" type="button" onClick={() => setViewId(null)}>{t("staffConsole.material.backToCurrent")}</button>
          </p>
        )}
      </div>

      {!viewingOld && (
        <div className="alex-scroll">
          {/* 2. ADD: capture first - the mic is the door into the queue. */}
          {props.csrfToken && (
            <div className="alex-add">
              <WalkFeedback
                csrfToken={props.csrfToken}
                t={t}
                copyPrefix="staffConsole.walk"
                compact
                pointerRef={walkPointerRef}
                onRecordingChange={setWalkActive}
                onSubmit={async (payload) => {
                  const response = await props.run(
                    `/api/staff/clients/${props.detail.client.id}/deliverables/${String(current.id)}/feedback`,
                    { method: "POST", body: { text: payload.text ?? "", pointerTrace: payload.pointerTrace } }
                  );
                  return response.ok;
                }}
              />
              {typedOpen ? (
                <div className="alex-typed">
                  <textarea
                    ref={typedAreaRef}
                    value={typedNote}
                    onChange={(event) => setTypedNote(event.target.value)}
                    onKeyDown={(event) => {
                      // Escape closes ONLY the note; without stopping the
                      // event it bubbled to the overlay's window listener and
                      // tore down the whole review room (audit a2-04).
                      if (event.key === "Escape") {
                        event.stopPropagation();
                        closeTypedNote();
                      }
                    }}
                    placeholder={t("staffConsole.notes.placeholder")}
                    rows={3}
                  />
                  <div className="button-row">
                    <button className="secondary-button compact-button" type="button" disabled={!typedNote.trim()} onClick={() => void saveTypedNote()}>
                      {t("staffConsole.notes.addNote")}
                    </button>
                    <button className="quiet-link" type="button" onClick={closeTypedNote}>{t("sales.walk.cancel")}</button>
                  </div>
                </div>
              ) : (
                <button ref={typedToggleRef} className="quiet-link alex-typed-toggle" type="button" title={t("staffConsole.material.writeNote")} onClick={() => setTypedOpen(true)}>
                  {t("staffConsole.material.writeShort")}
                </button>
              )}
            </div>
          )}

          {/* 3. QUEUE: the notes carry the whole story in their state chips. */}
          <section className="alex-notes">
            <div className="alex-notes-head">
              <h3>{t("staffConsole.material.notesTitle")}{openNotes.length > 0 ? ` (${openNotes.length})` : ""}</h3>
              {openNoteIds.length > 1 && !processing && (
                <button
                  className="quiet-link"
                  type="button"
                  onClick={() => setSelectedNotes(allSelected ? new Set() : new Set(openNoteIds))}
                >
                  {t(allSelected ? "staffConsole.material.selectNone" : "staffConsole.material.selectAll")}
                </button>
              )}
            </div>
            {openNotes.length > 0
              ? <ol className="doc-notes-list">{openNotes.map(noteCard)}</ol>
              : <p className="staff-card-hint">{t("staffConsole.material.noOpenNotes")}</p>}
            {resolvedNotes.length > 0 && (
              <details className="alex-resolved">
                <summary>{t("staffConsole.material.resolvedTitle").replace("{count}", String(resolvedNotes.length))}</summary>
                <ol className="doc-notes-list">{resolvedNotes.map(noteCard)}</ol>
              </details>
            )}
          </section>
        </div>
      )}

      {/* 4. SEND: fixed footer - the primary action never scrolls away. */}
      {!viewingOld && (
        <footer className="alex-send">
          {messageOpen ? (
            <div className="alex-typed">
              {/* One dictation pattern everywhere (finding 16): the orb IS the
                  control - mic left of the textarea, stop square while it
                  records, no labeled stop button. */}
              <div className="alex-dictate-row">
                <button
                  type="button"
                  className={`voice-inline-mic composer-orb${voicePhase === "recording" ? " recording" : ""}`}
                  aria-label={t(voicePhase === "recording" ? "voice.finish" : "voice.start")}
                  title={t(voicePhase === "recording" ? "voice.finish" : "voice.start")}
                  disabled={voicePhase === "transcribing"}
                  onClick={() => { if (voicePhase === "recording") recorderRef.current?.stop(); else void startVoice(); }}
                >
                  {voicePhase === "recording" ? (
                    <span className="voice-orb-stop" aria-hidden="true" />
                  ) : (
                    <svg className="voice-orb-mic" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path fill="currentColor" d="M12 14.5a3.3 3.3 0 0 0 3.3-3.3V6.3a3.3 3.3 0 1 0-6.6 0v4.9a3.3 3.3 0 0 0 3.3 3.3Zm5.8-3.3a5.8 5.8 0 0 1-11.6 0H4.4a7.6 7.6 0 0 0 6.7 7.5v2.5h1.8v-2.5a7.6 7.6 0 0 0 6.7-7.5h-1.8Z" />
                    </svg>
                  )}
                </button>
                <textarea
                  ref={messageAreaRef}
                  value={instruction}
                  onChange={(event) => setInstruction(event.target.value)}
                  onKeyDown={(event) => {
                    // Same Escape scope as the typed note: close the message
                    // disclosure, keep the room open (audit a2-04).
                    if (event.key === "Escape") {
                      event.stopPropagation();
                      closeMessage();
                    }
                  }}
                  placeholder={t("staffConsole.material.placeholder")}
                  rows={3}
                />
              </div>
              <div className="button-row">
                {voicePhase === "transcribing" && <small className="composer-voice-status" role="status">{t("voice.transcribing")}</small>}
                {voicePhase !== "transcribing" && voiceFailure && (
                  <small className="composer-voice-status message error-message" role="alert">{t(voiceFailureMessageKey(voiceFailure))}</small>
                )}
                <button className="quiet-link" type="button" onClick={closeMessage}>{t("sales.walk.cancel")}</button>
              </div>
            </div>
          ) : (
            <button ref={messageToggleRef} className="quiet-link alex-typed-toggle" type="button" onClick={() => setMessageOpen(true)}>
              {instruction.trim() ? t("staffConsole.material.messageEdit") : t("staffConsole.material.messageToggle")}
            </button>
          )}
          <button
            aria-describedby={sendReason ? "alex-send-reason" : undefined}
            className="primary-button alex-send-button"
            type="button"
            disabled={sending || processing || sendEmpty}
            title={sendReason ? t("staffConsole.material.sendNeeds") : undefined}
            onClick={() => void sendInstruction()}
          >
            {sending ? t("staffConsole.salesEdit.sending") : processing ? t("staffConsole.material.workingShort") : sendLabel}
          </button>
          {sendReason && (
            <p className="alex-send-reason" id="alex-send-reason">{t("staffConsole.material.sendNeeds")}</p>
          )}
          {sendError && <p className="message error-message" role="alert">{t("common.error")}</p>}
        </footer>
      )}
    </div>
  );

  return (
    <DocOverlay
      title={String(current.title ?? "")}
      kicker={makeSafeT(t)(`sales.kind.${props.kind}`, props.kind)}
      meta={docMetaLine(t, viewed.version, viewed.updatedAt)}
      closeLabel={t("sales.doc.close")}
      t={t}
      onClose={props.onClose}
      closeButtonRef={closeButtonRef}
      immersive={!contentMd}
      printable={Boolean(contentMd)}
      panelLabel={t("staffConsole.material.panelLabel")}
      aside={aside}
      workflow
      walkActive={walkActive}
      onContentPointerMove={(event) => { if (walkActive) walkPointerRef.current?.(walkPointFromEvent(event)); }}
    >
      {contentMd ? (
        showDiff && previousMd
          ? <DocDiffView previous={previousMd} current={contentMd} legend={t("staffConsole.material.diffLegend")} />
          // The e-mail deliverable reads as a CAMPAIGN on every surface that
          // can open it. The D12 copy contract used to live only in the
          // client's tile, so the partner (the ONLY reader of a material that
          // is not published yet) opened an e-mail here and found no way to
          // copy it (Pavel 2026-08-05). Version diffs stay a diff.
          : props.kind === "emails"
            ? <EmailsCopyView md={contentMd} t={t} />
            : <MarkdownView md={contentMd} />
      ) : (
        <div className="doc-review-frame-wrap immersive">
          <iframe ref={frameRef} className="doc-review-frame" src={url} title={String(current.title ?? "")} sandbox="allow-same-origin allow-scripts" />
          {walkActive && <WalkCaptureLayer frameRef={frameRef} onPoint={(point) => walkPointerRef.current?.(point)} />}
        </div>
      )}
    </DocOverlay>
  );
}

export function restoreFocus(
  target: Pick<HTMLElement, "focus" | "isConnected"> | null,
  fallbackSelector?: string
): void {
  if (!target && !fallbackSelector) return;
  const attempt = () => {
    if (target?.isConnected) {
      target.focus();
      return;
    }
    if (fallbackSelector) {
      document.querySelector<HTMLElement>(fallbackSelector)?.focus();
    }
  };
  queueMicrotask(attempt);
  // The first attempt can race the re-render that re-attaches the opener;
  // retry once after paint if focus fell through to the body (audit a2-04).
  if (typeof window !== "undefined") {
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body || document.activeElement === null) attempt();
    });
  }
}

export function focusReviewRoom(target?: HTMLElement | null): void {
  (target ?? document.querySelector<HTMLElement>(".doc-overlay-close"))?.focus({ preventScroll: true });
}

export function trapReviewRoomFocus(event: KeyboardEvent): void {
  if (event.key !== "Tab") return;
  const root = document.querySelector<HTMLElement>(".doc-overlay");
  if (!root) return;
  const controls = [...root.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )].filter((element) => element.getClientRects().length > 0);
  const first = controls[0];
  const last = controls.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}
